import classNames from 'classnames'
import { createRef, useEffect, useRef } from 'react'
import BSCard from 'react-bootstrap/Card'
import { useCookies } from 'react-cookie'
import { forceCheck } from 'react-lazyload'

import cardService from '@/services/card.service'
import { groupBy } from '@/store/cardlists/actions/groupBy'
import { sortBy } from '@/store/cardlists/actions/sortBy'
import { SET_STATE } from '@/store/cardlists/actions/types'
import { useAppSelector } from '@/store/hooks'
import { useActionless } from '@/store/hooks'
import { CardListType, CardType, PageCardType } from '@/types/page'
import { defaultGroupedBy, defaultSortedBy } from '@/utils/constants'
import { cardListsToNames } from '@/utils/helpers'

import Spinner from '../Spinner/Spinner'

import AdditionalFilters from './AdditionalFilters/AdditionalFilters'
import styles from './CardLists.module.scss'
import CardListsHeader from './CardListsHeader/CardListsHeader'
import CardView from './CardView/CardView'
import TableView from './TableView/TableView'
import TextView from './TextView/TextView'

type Props = {
  card?: PageCardType
  cardViewOnly?: boolean
  hideControls?: boolean
  initCardLists?: CardListType[]
  initCardMap?: Record<string, CardType>
  initGroupedBy?: string
  initSortedBy?: string
  sortedCardLists?: CardListType[]
  topLeftEdge?: boolean
}

const CardLists = ({
  card,
  cardViewOnly,
  hideControls,
  initCardLists,
  initCardMap,
  initGroupedBy,
  initSortedBy,
  sortedCardLists,
  topLeftEdge,
}: Props) => {
  const cardListsState = useAppSelector((state) => state.cardlists)
  const { cardLists: storeCardLists, loading, updatedAt } = cardListsState
  const [cookies, _] = useCookies(['cardListView'])
  const [initStore] = useActionless(SET_STATE)
  const ref = useRef<HTMLAnchorElement>(null)
  const view = cookies.cardListView || 'card'

  const scrollToCardLists = () => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth' })
  }

  // Recheck LazyLoad elements on window resize
  useEffect(() => {
    window.addEventListener('resize', forceCheck)
    return () => window.removeEventListener('resize', forceCheck)
  }, [])

  // Initialize card cache
  useEffect(() => {
    if (initCardMap && Object.keys(initCardMap).length > 0) {
      cardService.updateCardCache(initCardMap)
    } else if (initCardLists) {
      cardService.getCardMap(cardListsToNames(initCardLists))
    }
  }, [initCardMap])

  // Initialize cardlists store with server-side data
  useEffect(() => {
    if (initCardLists) {
      const initStoreValues = {
        cardLists: sortedCardLists || initCardLists,
        initCardLists,
        updatedAt: Date.now(),
      } as any

      if (initGroupedBy) initStoreValues.groupedBy = initGroupedBy
      if (initSortedBy) initStoreValues.sortedBy = initSortedBy

      initStore(initStoreValues)

      if (!sortedCardLists) {
        // Server received cookie but didn't group/sort, so we need to do it here
        if (initGroupedBy && initGroupedBy !== defaultGroupedBy) groupBy(initGroupedBy)
        if (initSortedBy && initSortedBy !== defaultSortedBy) sortBy(initSortedBy)
      }
    }
  }, [initCardLists])

  // Recheck LazyLoad elements on cardlist update
  useEffect(() => {
    if (updatedAt) forceCheck()
  }, [updatedAt])

  if (!initCardLists) return null

  const cardLists = updatedAt ? storeCardLists : initCardLists
  const sectionRefs = cardLists.map(() => createRef<HTMLDivElement>())

  return (
    <BSCard
      className={classNames('mvCardList shadow-sm', styles.mvCardList)}
      style={{
        borderTopLeftRadius: topLeftEdge ? 0 : undefined,
        borderTopRightRadius: view === 'text' ? 0 : undefined,
      }}
    >
      <a className={styles.top} id='cardlists' ref={ref} />
      <div>
        <AdditionalFilters initCardLists={initCardLists} />
        <CardListsHeader
          cardLists={cardLists}
          hideControls={hideControls}
          hideHeader={view !== 'card'}
          initGroupedBy={initGroupedBy}
          initSortedBy={initSortedBy}
          sectionRefs={sectionRefs}
        />
        {loading && (
          <div className={styles.spinner}>
            <Spinner size='lg' />
          </div>
        )}
        {cardLists.length === 0 && <div className={styles.noResults}>No results</div>}
        {(cardViewOnly || view === 'card') && (
          <CardView
            cardLists={cardLists}
            hideControls={hideControls}
            initGroupedBy={initGroupedBy}
            initSortedBy={initSortedBy}
            pageCommander={card}
            scrollToCardLists={scrollToCardLists}
            sectionRefs={sectionRefs}
          />
        )}
        {!cardViewOnly && view === 'text' && <TextView cardLists={cardLists} />}
        {!cardViewOnly && view === 'table' && <TableView cardLists={cardLists} />}
      </div>
    </BSCard>
  )
}

export default CardLists
